"use client";

import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from "react";
import { trackEvent } from "@/lib/analytics";

const DEMO_SUBMITTED_KEY = "vetina-demo-submitted";

interface DemoPopupContextType {
  isOpen: boolean;
  hasSubmitted: boolean;
  source: string | null;
  openPopup: (source?: string) => void;
  closePopup: () => void;
  markAsSubmitted: () => void;
}

const DemoPopupContext = createContext<DemoPopupContextType | undefined>(undefined);

export function DemoPopupProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [source, setSource] = useState<string | null>(null);

  // Check sessionStorage on mount
  useEffect(() => {
    const submitted = sessionStorage.getItem(DEMO_SUBMITTED_KEY);
    if (submitted === "true") {
      setHasSubmitted(true);
    }
  }, []);

  const openPopup = useCallback((src?: string) => {
    // Don't open if already submitted this session
    if (sessionStorage.getItem(DEMO_SUBMITTED_KEY) === "true") {
      return;
    }

    setSource(src ?? null);
    setIsOpen(true);
    trackEvent({
      event: "callback_popup_opened",
      category: "engagement",
      label: src || "unknown",
    });
  }, []);

  const closePopup = useCallback(() => {
    setIsOpen(false);
  }, []);

  const markAsSubmitted = useCallback(() => {
    sessionStorage.setItem(DEMO_SUBMITTED_KEY, "true");
    setHasSubmitted(true);
  }, []);

  return (
    <DemoPopupContext.Provider value={{ isOpen, hasSubmitted, source, openPopup, closePopup, markAsSubmitted }}>
      {children}
    </DemoPopupContext.Provider>
  );
}

export function useDemoPopup() {
  const context = useContext(DemoPopupContext);
  if (context === undefined) {
    throw new Error("useDemoPopup must be used within a DemoPopupProvider");
  }
  return context;
}
